import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete, Tooltip } from 'antd';
import { debounce } from 'lodash';
import { getLocationSuggestions, getLocationById, getDisplayNameFromId } from '../utils/findLocation';
import { HistoryOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';

const LocationSelect = ({ value, onChange, placeholder = "Enter a location" }) => {
    const [options, setOptions] = useState([]);
    const [displayValue, setDisplayValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [focused, setFocused] = useState(false);
    const mounted = useRef(true);
    const { locations: historyLocations } = useSearchSuggestions();
    
    // Track both locationId and displayName internally
    const locationDataRef = useRef({ locationId: null, displayName: '' });





    // Create a debounced search function that respects the mounted state
    const debouncedSearch = useRef(
        debounce((searchText) => {
            if (!mounted.current) return;
            
      
            
            if (!searchText) {
                // If empty and focused, show history suggestions
                if (focused && historyLocations && historyLocations.length > 0) {
                
                    const historyOptions = historyLocations.slice(0, 5).map(location => {
                        // Check if location is a structured object from history
                        if (typeof location === 'object' && location.name && location.id) {
                            return {
                                value: location.name,
                                label: (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{location.name}</span>
                                        <Tooltip title="From your search history">
                                            <HistoryOutlined style={{ color: '#1890ff' }} />
                                        </Tooltip>
                                    </div>
                                ),
                                isHistory: true,
                                locationId: location.id
                            };
                        }
                        // Otherwise treat as string
                        return {
                            value: location,
                            label: (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{location}</span>
                                    <Tooltip title="From your search history">
                                        <HistoryOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                </div>
                            ),
                            isHistory: true
                        };
                    });
                    setOptions(historyOptions);
                } else {
                    setOptions([]);
                }
                setLoading(false);
                return;
            }

            setLoading(true);
            
            // First check if we have matching history locations
            let historyMatches = [];
            if (historyLocations && historyLocations.length > 0) {
        
                historyMatches = historyLocations
                    .filter(location => {
                        const locationStr = typeof location === 'object' && location.name 
                            ? location.name 
                            : String(location);
                        return locationStr.toLowerCase().includes(searchText.toLowerCase());
                    })
                    .slice(0, 3) // Limit to top 3 matches
                    .map(location => {
                        // Check if location is a structured object
                        if (typeof location === 'object' && location.name && location.id) {
                            return {
                                value: location.name,
                                label: (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{location.name}</span>
                                        <Tooltip title="From your search history">
                                            <HistoryOutlined style={{ color: '#1890ff' }} />
                                        </Tooltip>
                                    </div>
                                ),
                                isHistory: true,
                                locationId: location.id
                            };
                        }
                        // Otherwise treat as string
                        return {
                            value: location,
                            label: (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{location}</span>
                                    <Tooltip title="From your search history">
                                        <HistoryOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                </div>
                            ),
                            isHistory: true
                        };
                    });
                
             
            }
            
            try {
             
                const locations = getLocationSuggestions(searchText);
           
                
                const apiOptions = locations.map(location => ({
                    value: location.value, // Use the display value for the dropdown value
                    label: (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{location.value}</span>
                            <EnvironmentOutlined style={{ color: '#52c41a' }} />
                        </div>
                    ),
                    locationId: location.locationId,
                    displayName: location.value
                }));
                
                if (mounted.current) {
                    // Combine history matches with API results
                    const allOptions = [...historyMatches, ...apiOptions];
                
                    setOptions(allOptions);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error searching locations:', error);
                if (mounted.current) {
               
                    setOptions(historyMatches);
                    setLoading(false);
                }
            }
        }, 500)
    ).current;

    useEffect(() => {
        // Initialize display value based on the provided value
        if (value) {
          
            
            if (typeof value === 'object' && value.name) {
                // Handle object with name property
                setDisplayValue(value.name);
                locationDataRef.current = { 
                    locationId: value.locationId || value.id || null, 
                    displayName: value.name 
                };
            } else if (typeof value === 'string') {
                // For string values, check if it's a numeric ID
                if (/^\d+$/.test(value)) {
                    // It's a numeric ID as string, look up the display name
                    const displayName = getDisplayNameFromId(value);
                    setDisplayValue(displayName);
                    locationDataRef.current = { 
                        locationId: value, 
                        displayName 
                    };
                } else {
                    // It's a regular string location name
                    setDisplayValue(value);
                    locationDataRef.current = { 
                        locationId: null,
                        displayName: value 
                    };
                }
            } else if (typeof value === 'number') {
                // For numeric values, convert to string ID and look up display name
                const idStr = String(value);
                const displayName = getDisplayNameFromId(idStr);
                setDisplayValue(displayName);
                locationDataRef.current = { 
                    locationId: idStr, 
                    displayName 
                };
            } else {
                // For any other type, just use empty string
                setDisplayValue('');
                locationDataRef.current = { locationId: null, displayName: '' };
            }
        } else {
            setDisplayValue('');
            locationDataRef.current = { locationId: null, displayName: '' };
        }
    }, [value]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            mounted.current = false;
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearch = (text) => {
      
        setDisplayValue(text);
        debouncedSearch(text);
    };

    const handleChange = (text) => {
      
        setDisplayValue(text);
        // If user clears the input, reset the value
        if (!text) {
            locationDataRef.current = { locationId: null, displayName: '' };
            onChange(''); // Pass empty string instead of undefined to maintain controlled component
        } else {
            // If user is typing, update the display name but keep the ID if it exists
            locationDataRef.current.displayName = text;
            // Pass the string value for the search to work correctly
            onChange(text);
        }
    };

    const handleFocus = () => {
      
        setFocused(true);
        // Show history suggestions when focused with empty input
        if (!displayValue && historyLocations && historyLocations.length > 0) {
            const historyOptions = historyLocations.slice(0, 5).map(location => {
                // Check if location is a structured object
                if (typeof location === 'object' && location.name && location.id) {
                    return {
                        value: location.name,
                        label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{location.name}</span>
                                <Tooltip title="From your search history">
                                    <HistoryOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            </div>
                        ),
                        isHistory: true,
                        locationId: location.id
                    };
                }
                // Otherwise treat as string
                return {
                    value: location,
                    label: (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{location}</span>
                            <Tooltip title="From your search history">
                                <HistoryOutlined style={{ color: '#1890ff' }} />
                            </Tooltip>
                        </div>
                    ),
                    isHistory: true
                };
            });
            setOptions(historyOptions);
        }
    };

    const handleBlur = () => {
       
        setFocused(false);
    };

    const handleSelect = (value, option) => {
       
        setDisplayValue(value); // Use the display value for the input field
        
        // Store both ID and display name
        if (option.locationId) {
            locationDataRef.current = {
                locationId: option.locationId,
                displayName: value
            };
            // Create a structured object with both ID and name to pass to the form
            const locationObject = {
                locationId: option.locationId,
                name: value
            };
            // Pass the structured object to keep both the ID and name together
            onChange(locationObject);
        } else {
            locationDataRef.current = {
                locationId: null,
                displayName: value
            };
            // If there's no ID, pass the display value
            onChange(value);
        }
        

    };

    return (
        <AutoComplete
            value={displayValue}
            options={options}
            onSearch={handleSearch}
            onChange={handleChange}
            onSelect={handleSelect}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            style={{ width: '100%' }}
            notFoundContent={loading ? 'Loading...' : 'No locations found'}
            allowClear
        />
    );
};

export default LocationSelect; 